import { Stack } from "@mui/material";
import { omit } from "lodash-es";
import type { FC } from "react";
import { prettyChannelName } from "../../../../channels/utils";
import { PreviewMissingButton } from "./components/preview-missing-button";
import { PreviewPersonsButton } from "./components/preview-persons-button";
import { PreviewTooltip } from "./components/preview-tooltip";
import { usePersonsAndMissing } from "./hooks/use-persons-and-missing";
import { PreviewDatagrid } from "./preview-datagrid";
import type { PreviewProps } from "./types";
import { subjectToChange } from "./utils";
import { useOptedOutPersons } from "./hooks/use-opted-out-persons";
import { PreviewOptedOutButton } from "./components/preview-opt-out-button";
import { useFlag } from "../../../../../server/optimizely";

export const SegmentPreview: FC<PreviewProps<"segment">> = ({ data, channel, ...props }) => {
  const { persons, missing } = usePersonsAndMissing(channel, data);
  const optedOutPersons = useOptedOutPersons(channel, data);
  const [optOutsEnabled] = useFlag("opt_outs");
  const channelName = prettyChannelName(channel);

  const recipientLabel = persons.length === 1 ? "recipient" : "recipients";
  const peopleLabel = data.length === 1 ? "person" : "people";
  const previewTitle =
    `${persons.length} ${channelName} ${recipientLabel} for ` +
    `${data.length} ${peopleLabel} in the segment. ` +
    subjectToChange;

  const missingPeopleLabel = missing.length === 1 ? "person" : "people";
  const missingVerb = missing.length === 1 ? "doesn't" : "don't";
  const missingClickableText = `${missing.length} ${missingPeopleLabel} in this segment`;
  const missingSuffixText = `${missingVerb} have a ${channelName} connected`;

  const optedOutPeopleLabel = optedOutPersons.length === 1 ? "person" : "people";
  const optedOutVerb = optedOutPersons.length === 1 ? "has" : "have";
  const optedOutClickableText = `${optedOutPersons.length} ${optedOutPeopleLabel} in this segment`;
  const optedOutSuffixText = `${optedOutVerb} opted out of ${channelName}`;

  return (
    <>
      <Stack
        direction="column"
        spacing={0.5}
        alignItems="flex-end"
        {...omit(props, "preview_config")}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PreviewPersonsButton
            persons={persons.length}
            channel={channel}
            buttonText="Recipients: "
          >
            <PreviewDatagrid channelName={channel} persons={persons} />
          </PreviewPersonsButton>
          <PreviewTooltip title={previewTitle} />
        </Stack>
        {missing.length > 0 && (
          <PreviewMissingButton
            clickableText={missingClickableText}
            suffixText={missingSuffixText}
            channelName={channel}
          >
            <PreviewDatagrid channelName={null} persons={missing} />
          </PreviewMissingButton>
        )}
        {optOutsEnabled && optedOutPersons.length > 0 && (
          <PreviewOptedOutButton
            clickableText={optedOutClickableText}
            suffixText={optedOutSuffixText}
            channelName={channel}
          >
            <PreviewDatagrid channelName={channel} persons={optedOutPersons} />
          </PreviewOptedOutButton>
        )}
      </Stack>
    </>
  );
};
